import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Box,
  MenuItem,
  Typography
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Budget, BudgetCategory, DEFAULT_CATEGORIES } from '../../types';
import { budgets } from '../../services/api';

interface NewBudgetDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (budget: Omit<Budget, '_id' | 'id' | 'user' | 'createdAt'>) => void;
}

const NewBudgetDialog: React.FC<NewBudgetDialogProps> = ({ open, onClose, onSubmit }) => {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [newCategory, setNewCategory] = useState<Partial<BudgetCategory>>({
    name: '',
    budgetedAmount: 0,
    type: 'expense'
  });

  useEffect(() => {
    if (open) {
      // Set current month when dialog opens
      const now = new Date();
      setMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
      
      // Load previous month's budget
      const loadPreviousMonthBudget = async () => {
        try {
          const allBudgets = await budgets.getAll();
          const sortedBudgets = allBudgets.sort((a: Budget, b: Budget) => 
            new Date(b.month).getTime() - new Date(a.month).getTime()
          );
          
          if (sortedBudgets.length > 0) {
            // Get the categories from the most recent budget
            const previousCategories = sortedBudgets[0].categories.map((cat: BudgetCategory) => ({
              name: cat.name,
              type: cat.type,
              budgetedAmount: 0, // Reset budget amount instead of copying from previous month
              actualAmount: 0, // Reset actual amount for the new budget
              sortOrder: cat.sortOrder
            }));
            setCategories(previousCategories);
          } else {
            // If no previous budget exists, use default categories
            setCategories(DEFAULT_CATEGORIES.map(cat => ({ ...cat, actualAmount: 0 })));
          }
        } catch (error) {
          console.error('Error loading previous month budget:', error);
          // If there's an error, fall back to default categories
          setCategories(DEFAULT_CATEGORIES.map(cat => ({ ...cat, actualAmount: 0 })));
        }
      };

      loadPreviousMonthBudget();
    }
  }, [open]);

  const handleAddCategory = () => {
    if (!newCategory.name || !newCategory.type) return;
    if ((newCategory.type === 'expense' || newCategory.type === 'savings') && !newCategory.budgetedAmount) return;

    setCategories([
      ...categories,
      {
        name: newCategory.name,
        budgetedAmount: newCategory.type === 'income' ? 0 : Number(newCategory.budgetedAmount),
        actualAmount: 0,
        type: newCategory.type as 'income' | 'expense' | 'savings'
      }
    ]);

    setNewCategory({
      name: '',
      budgetedAmount: 0,
      type: 'expense'
    });
  };

  const handleRemoveCategory = (index: number) => {
    setCategories(categories.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (categories.length === 0) return;

    const totalBudgeted = categories
      .filter(cat => cat.type === 'expense' || cat.type === 'savings')
      .reduce((sum, cat) => sum + cat.budgetedAmount, 0);

    const totalActual = categories
      .reduce((sum, cat) => sum + cat.actualAmount, 0);

    // Fix date handling to avoid timezone issues
    // Parse the month string (YYYY-MM) and create a date for the first day of that month
    const [year, monthNum] = month.split('-').map(Number);
    const monthDate = new Date(year, monthNum - 1, 1); // monthNum - 1 because JS months are 0-indexed

    onSubmit({
      month: monthDate.toISOString(),
      categories,
      totalBudgeted,
      totalActual
    });
    onClose();
  };

  const handleLoadTemplate = () => {
    setCategories(DEFAULT_CATEGORIES.map(cat => ({ ...cat, actualAmount: 0 })));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Budget</DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <TextField
            label="Month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            fullWidth
            required
            InputLabelProps={{
              shrink: true
            }}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="subtitle1">Categories</Typography>
          <Button
            size="small"
            onClick={handleLoadTemplate}
            color="primary"
          >
            Load Template
          </Button>
        </Box>

        <Box display="flex" gap={2} mb={2}>
          <TextField
            label="Category Name"
            value={newCategory.name}
            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
            size="small"
          />
          <TextField
            select
            label="Type"
            value={newCategory.type}
            onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value as 'income' | 'expense' | 'savings' })}
            size="small"
          >
            <MenuItem value="income">Income</MenuItem>
            <MenuItem value="expense">Expense</MenuItem>
            <MenuItem value="savings">Savings</MenuItem>
          </TextField>
          {(newCategory.type === 'expense' || newCategory.type === 'savings') && (
            <TextField
              label="Amount"
              type="number"
              value={newCategory.budgetedAmount}
              onChange={(e) =>
                setNewCategory({ ...newCategory, budgetedAmount: parseFloat(e.target.value) })
              }
              size="small"
            />
          )}
          <IconButton
            color="primary"
            onClick={handleAddCategory}
            disabled={!newCategory.name || ((newCategory.type === 'expense' || newCategory.type === 'savings') && !newCategory.budgetedAmount)}
          >
            <AddIcon />
          </IconButton>
        </Box>

        <List>
          {categories.map((category, index) => (
            <ListItem key={index}>
              <ListItemText
                primary={category.name}
                secondary={`${category.type.charAt(0).toUpperCase() + category.type.slice(1)} - ${category.type === 'income' ? 'Income' : `$${category.budgetedAmount}`}`}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleRemoveCategory(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={categories.length === 0}
        >
          Create Budget
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewBudgetDialog; 